import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Divider,
  Paper,
  Button,
  Link,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ContactMail as ContactMailIcon,
  Help as HelpIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from "@mui/icons-material";
import { ROUTES } from "../config/routes";
import { purposeService, DPOContactInfo } from "../services/purpose-service";

const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [dpoInfo, setDpoInfo] = useState<DPOContactInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDPOInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        const info = await purposeService.getDPOContact();
        setDpoInfo(info);
      } catch (err: any) {
        console.error("Error loading DPO info:", err);
        setError(err?.message || "Could not load DPO contact information");
        setDpoInfo(null);
      } finally {
        setLoading(false);
      }
    };

    loadDPOInfo();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        Support & Contact
      </Typography>

      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        {/* DPO Contact Information */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <ContactMailIcon color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Data Protection Officer (DPO) Contact
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>DPO Contact Information</strong>
                </Typography>
                <Typography variant="body2" paragraph>
                  {error}
                </Typography>
                <Typography variant="body2" paragraph>
                  To view the Data Protection Officer contact information, please:
                </Typography>
                <Box component="ol" sx={{ mt: 1, mb: 0, pl: 3 }}>
                  <li>
                    Go to the{" "}
                    <Link onClick={() => navigate(ROUTES.CONSENTS)} sx={{ cursor: "pointer", fontWeight: 500 }}>
                      Forms
                    </Link>{" "}
                    page
                  </li>
                  <li>Click on any consent form</li>
                  <li>Navigate to the "Contact DPO" tab</li>
                </Box>
              </Alert>
            ) : dpoInfo ? (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {dpoInfo.title}
                </Typography>
                {dpoInfo.effectiveDate && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {dpoInfo.effectiveDate}
                    {dpoInfo.lastUpdated && ` • ${dpoInfo.lastUpdated}`}
                  </Typography>
                )}
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    "& h2": { mt: 3, mb: 2, color: "primary.main" },
                    "& h3": { mt: 2, mb: 1, color: "text.primary" },
                    "& p": { mb: 2, lineHeight: 1.6 },
                    "& ul": { mb: 2, pl: 2 },
                    "& li": { mb: 1 },
                  }}
                  dangerouslySetInnerHTML={{ __html: dpoInfo.content }}
                />
              </Box>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>DPO Contact Information</strong>
                </Typography>
                <Typography variant="body2" paragraph>
                  DPO contact information is not currently available. To view contact information, please:
                </Typography>
                <Box component="ol" sx={{ mt: 1, mb: 0, pl: 3 }}>
                  <li>
                    Go to the{" "}
                    <Link onClick={() => navigate(ROUTES.CONSENTS)} sx={{ cursor: "pointer", fontWeight: 500 }}>
                      Forms
                    </Link>{" "}
                    page
                  </li>
                  <li>Click on any consent form</li>
                  <li>Navigate to the "Contact DPO" tab</li>
                </Box>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <QuestionAnswerIcon color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Frequently Asked Questions
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />

            <Box>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    How do I view my personal data?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    You can view your personal data in two ways:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                    <li>
                      <strong>Forms:</strong> View all consent forms you've submitted, including the data you provided at the time of consent.
                    </li>
                    <li>
                      <strong>Data Elements:</strong> View a consolidated list of all data elements we have collected about you, including the latest
                      values and collection history.
                    </li>
                  </Box>
                  <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                    <Button variant="outlined" size="small" startIcon={<DescriptionIcon />} onClick={() => navigate(ROUTES.CONSENTS)}>
                      View Forms
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => navigate(ROUTES.DATA)}>
                      View Data Elements
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    How do I submit a data access request?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    You can submit a formal data access request in two ways:
                  </Typography>
                  <Box component="ol" sx={{ mt: 1, mb: 0, pl: 2 }}>
                    <li>
                      Go to the{" "}
                      <Link onClick={() => navigate(ROUTES.DATA)} sx={{ cursor: "pointer", fontWeight: 500 }}>
                        Data
                      </Link>{" "}
                      page
                    </li>
                    <li>Click on any data element card</li>
                    <li>Click the "New Request" button</li>
                    <li>Select "Data Access" as the request type</li>
                    <li>Fill in the description and submit</li>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    You can track the status of your requests on the{" "}
                    <Link onClick={() => navigate(ROUTES.REQUESTS)} sx={{ cursor: "pointer", fontWeight: 500 }}>
                      Requests
                    </Link>{" "}
                    page.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    What's the difference between viewing data and requesting data access?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Viewing data</strong> in the portal is an informal way to see what information we have about you. This is available
                    anytime through your portal account.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Data Access Request</strong> is a formal legal request under the DPDP Act. When you submit a data access request:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                    <li>We are legally obligated to respond within 30 days</li>
                    <li>You receive a formal response with your data in a structured format</li>
                    <li>The request is tracked and logged for compliance purposes</li>
                    <li>You can download or export your data in a portable format</li>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    How do I correct inaccurate data?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    To request correction of inaccurate data:
                  </Typography>
                  <Box component="ol" sx={{ mt: 1, mb: 2, pl: 2 }}>
                    <li>
                      Go to the{" "}
                      <Link onClick={() => navigate(ROUTES.DATA)} sx={{ cursor: "pointer", fontWeight: 500 }}>
                        Data
                      </Link>{" "}
                      page
                    </li>
                    <li>Find the data element with incorrect information</li>
                    <li>Click on the data element card</li>
                    <li>Click "New Request" and select "Data Correction"</li>
                    <li>Describe what needs to be corrected and provide the correct information</li>
                    <li>Submit your request</li>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    We will review and process your correction request within 30 days as required by law.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    How do I withdraw my consent?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    To withdraw consent for a specific purpose:
                  </Typography>
                  <Box component="ol" sx={{ mt: 1, mb: 2, pl: 2 }}>
                    <li>
                      Go to the{" "}
                      <Link onClick={() => navigate(ROUTES.PURPOSES)} sx={{ cursor: "pointer", fontWeight: 500 }}>
                        Purposes
                      </Link>{" "}
                      page
                    </li>
                    <li>Find the purpose you want to withdraw consent for</li>
                    <li>Click on the purpose to view details</li>
                    <li>Click "Withdraw Consent"</li>
                    <li>Provide a reason (optional) and confirm</li>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Once withdrawn, we will stop processing your data for that purpose, subject to any legal retention requirements.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    How long does it take to process my request?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Under the Digital Personal Data Protection Act (DPDPA) 2023, we are required to respond to your requests within specific
                    timeframes:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, mb: 2, pl: 2 }}>
                    <li>
                      <strong>Data Access Requests:</strong> Within 30 days (may be extended by 30 days with notice)
                    </li>
                    <li>
                      <strong>Data Correction Requests:</strong> Within 30 days
                    </li>
                    <li>
                      <strong>Data Erasure Requests:</strong> Within 30 days (subject to legal retention requirements)
                    </li>
                    <li>
                      <strong>Data Portability Requests:</strong> Within 30 days
                    </li>
                    <li>
                      <strong>Consent Withdrawal:</strong> Processed immediately, with data processing stopped within 30 days
                    </li>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    You can track the status of your requests on the{" "}
                    <Link onClick={() => navigate(ROUTES.REQUESTS)} sx={{ cursor: "pointer", fontWeight: 500 }}>
                      Requests
                    </Link>{" "}
                    page.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    How do I contact the Data Protection Officer (DPO)?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    You can contact our Data Protection Officer using the contact information displayed above on this page. The DPO can help you with:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, mb: 2, pl: 2 }}>
                    <li>Privacy-related questions and concerns</li>
                    <li>Exercising your data protection rights</li>
                    <li>Filing complaints about data processing</li>
                    <li>Reporting data security incidents</li>
                    <li>Understanding how we use your personal data</li>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    You can also find DPO contact information in each of your consent forms by navigating to the "Contact DPO" tab.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    What happens to my data if I request erasure?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    When you request data erasure (also known as the "right to be forgotten"), we will:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, mb: 2, pl: 2 }}>
                    <li>Review your request and verify your identity</li>
                    <li>Assess if there are any legal obligations that require us to retain the data</li>
                    <li>Delete or anonymize your personal data where legally permitted</li>
                    <li>Notify you once the erasure is complete</li>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Note:</strong> We may be required to retain certain data for legal compliance, dispute resolution, or to fulfill
                    contractual obligations. In such cases, we will inform you of the reason for retention.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    To submit an erasure request, go to the{" "}
                    <Link onClick={() => navigate(ROUTES.DATA)} sx={{ cursor: "pointer", fontWeight: 500 }}>
                      Data
                    </Link>{" "}
                    page and create a "Data Erasure" request.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          </CardContent>
        </Card>

        {/* Help & Resources */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <HelpIcon color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Help & Resources
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Your Privacy Rights
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  As a data principal, you have the right to:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li>Access your personal data</li>
                  <li>Correct inaccurate data</li>
                  <li>Request erasure of your data</li>
                  <li>Withdraw consent</li>
                  <li>Request data portability</li>
                </Box>
                <Button variant="outlined" size="small" onClick={() => navigate(ROUTES.REQUESTS)} sx={{ mt: 2 }}>
                  View Your Requests
                </Button>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  View Your Data
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Review all the personal data we have collected about you, including forms, purposes, and data elements.
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button variant="outlined" size="small" startIcon={<DescriptionIcon />} onClick={() => navigate(ROUTES.CONSENTS)}>
                    View Forms
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => navigate(ROUTES.DATA)}>
                    View Data Elements
                  </Button>
                </Box>
              </Paper>
            </Box>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Need more help?</strong> If you have questions about your privacy rights or need assistance with your data, please contact the
            Data Protection Officer using the information available in your consent forms, or visit your consent forms to find detailed contact
            information.
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default SupportPage;
